import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
} from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MessageSet } from 'src/app/core/models/message-set.model';
import { Message } from 'src/app/core/models/message.model';
import { MessageChoice } from 'src/app/core/models/message-choice.model';
import { MessageImage } from 'src/app/core/models/message-image.model';
import { MessageVideo } from 'src/app/core/models/message-video.model';
import { MediaLibraryComponent } from '../media-library/media-library.component';
import { MediaType, ImageOrVideo } from 'src/app/core/types/types';
import { MessageSetPreviewDialogComponent } from './message-set-preview-dialog/message-set-preview-dialog.component';

// Deep enough to sever every array and object the builder writes to:
// the messages array, each message, and each message's choices/images/videos.
// Written out rather than structuredClone because MessageChoice is a class and
// MessageSet.created is a Date - a JSON round trip would flatten both.
export function cloneMessage(source: Message): Message {
  return {
    ...source,
    choices: (source.choices ?? []).map((choice) => ({ ...choice })) as any,
    images: [...((source.images ?? []) as any[])],
    videos: [...((source.videos ?? []) as any[])],
  };
}

export function cloneMessageSet(source: MessageSet): MessageSet {
  return {
    ...source,
    messages: (source.messages ?? []).map(cloneMessage),
  };
}

@Component({
  selector: 'cm-message-set-builder',
  templateUrl: './message-set-builder.component.html',
  styleUrls: ['./message-set-builder.component.scss'],
})
export class MessageSetBuilderComponent implements OnInit, OnChanges {
  @Input() messageSet: MessageSet;
  @Input() showVisibility: boolean = true;
  @Input() showOwnerSelect: boolean = false;
  @Input() customerAdmins: any[] = [];

  @Output() save = new EventEmitter<MessageSet>();
  @Output() cancel = new EventEmitter<void>();

  // Everything the builder edits happens here, never on the @Input.  The
  // container hands over an object whose messages array is shared with NgRx
  // state, and push/splice/reorder/reindex would otherwise write straight
  // into the store.
  workingSet: MessageSet;

  messageSetForm: FormGroup;
  currentMessageForm: FormGroup;
  currentMessage: Message;
  masterCurrentMessage: Message;
  selectedMessageIndex: number = -1;
  isMessageListCollapsed: boolean = false;

  private readonly SUBJECT_MAX_LENGTH_WITH_MEDIA = 40;
  private readonly SUBJECT_MAX_LENGTH_WITHOUT_MEDIA = 55;
  private readonly BODY_MAX_LENGTH_WITH_MEDIA = 180;
  private readonly BODY_MAX_LENGTH_WITHOUT_MEDIA = 235;
  private readonly MAX_CHOICES = 5;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['messageSet'];
    if (!change?.currentValue) {
      return;
    }
    const incoming = change.currentValue as MessageSet;
    // Only rebuild for a genuinely different set.  The same set re-emitting
    // (a list refresh, a save round trip) must not discard work in progress.
    if (change.firstChange || incoming.id !== this.workingSet?.id) {
      this.resetFrom(incoming);
    }
  }

  ngOnInit(): void {
    // Fallback for the case where no input was ever bound, so ngOnChanges
    // never ran and the forms would otherwise be undefined.
    if (!this.workingSet) {
      this.resetFrom(this.messageSet);
    }
  }

  private resetFrom(source: MessageSet): void {
    this.workingSet = cloneMessageSet(source ?? ({ messages: [] } as any));
    this.initializeMessageSetForm();
    this.initializeCurrentMessageForm();

    // A set that still needs a name opens on the field that blocks saving.
    this.setDetailsExpanded = this.messageSetForm.get('name').invalid;

    if (this.workingSet.messages?.length > 0) {
      this.selectMessage(0);
    } else {
      this.addBlankMessage();
    }
  }

  private initializeMessageSetForm(): void {
    this.messageSetForm = this.fb.group({
      name: [this.workingSet?.name || '', Validators.required],
      description: [this.workingSet?.description || ''],
      tag: [this.workingSet?.tag || ''],
      is_private: [this.workingSet?.is_private !== undefined ? this.workingSet.is_private : true],
      owner: [this.workingSet?.sender || null],
    });
  }

  private initializeCurrentMessageForm(): void {
    this.currentMessageForm = this.fb.group({
      subject: [''],
      body: ['', Validators.required],
      tag: [''],
      choices: this.fb.array([this.createChoiceFormGroup()]),
    });
  }

  private createChoiceFormGroup(choice?: MessageChoice): FormGroup {
    return this.fb.group({
      choice_text: [choice?.choice_text || '', Validators.required],
      tag: [choice?.tag || ''],
    });
  }

  get choicesFormArray(): FormArray {
    return this.currentMessageForm.get('choices') as FormArray;
  }

  // Message set methods
  get isVisibilityEnabled(): boolean {
    return true;
  }

  get isMessageSetDirty(): boolean {
    return this.messageSetForm.dirty;
  }

  // Current message methods
  buildBlankMessage(): Message {
    return {
      index: (this.workingSet?.messages?.length || 0) + 1,
      tag: '',
      subject: '',
      body: '',
      choices: [{ choice_text: '', tag: '', index: 1 } as any],
      images: [],
      videos: [],
    };
  }

  addBlankMessage(): void {
    this.currentMessage = this.buildBlankMessage();
    this.masterCurrentMessage = { ...this.currentMessage };
    this.selectedMessageIndex = -1;
    this.populateCurrentMessageForm(this.currentMessage);
    this.currentMessageForm.markAsPristine();
  }

  private populateCurrentMessageForm(message: Message): void {
    this.currentMessageForm.patchValue({
      subject: message.subject || '',
      body: message.body || '',
      tag: message.tag || '',
    });

    // Clear and rebuild choices
    while (this.choicesFormArray.length) {
      this.choicesFormArray.removeAt(0);
    }

    if (message.choices?.length > 0) {
      message.choices.forEach((choice) => {
        this.choicesFormArray.push(this.createChoiceFormGroup(choice as MessageChoice));
      });
    } else {
      this.choicesFormArray.push(this.createChoiceFormGroup());
    }
  }

  currentMessageHasMedia(): boolean {
    return (
      (this.currentMessage?.images?.length || 0) > 0 ||
      (this.currentMessage?.videos?.length || 0) > 0
    );
  }

  getSubjectMaxLength(): number {
    return this.currentMessageHasMedia()
      ? this.SUBJECT_MAX_LENGTH_WITH_MEDIA
      : this.SUBJECT_MAX_LENGTH_WITHOUT_MEDIA;
  }

  getBodyMaxLength(): number {
    return this.currentMessageHasMedia()
      ? this.BODY_MAX_LENGTH_WITH_MEDIA
      : this.BODY_MAX_LENGTH_WITHOUT_MEDIA;
  }

  getSubjectRemaining(): number {
    const subject = this.currentMessageForm.get('subject')?.value || '';
    return this.getSubjectMaxLength() - subject.length;
  }

  getBodyRemaining(): number {
    const body = this.currentMessageForm.get('body')?.value || '';
    return this.getBodyMaxLength() - body.length;
  }

  getSubjectPercentage(): number {
    const subject = this.currentMessageForm.get('subject')?.value || '';
    return (subject.length / this.getSubjectMaxLength()) * 100;
  }

  getBodyPercentage(): number {
    const body = this.currentMessageForm.get('body')?.value || '';
    return (body.length / this.getBodyMaxLength()) * 100;
  }

  toggleMessageList(): void {
    this.isMessageListCollapsed = !this.isMessageListCollapsed;
  }

  // ── Message row summaries ──
  // The collapsed row is now the only place a message is described, so it has
  // to carry enough to identify one without opening it.

  messageTitle(message: Message): string {
    return message.subject?.trim() || message.body?.trim() || '';
  }

  // A blank choice is always seeded in the editor, so a raw length would
  // report a choice that is not there.
  choiceCount(message: Message): number {
    return (message.choices || []).filter((choice) =>
      (choice as MessageChoice).choice_text?.trim()
    ).length;
  }

  mediaCount(message: Message): number {
    return (message.images?.length || 0) + (message.videos?.length || 0);
  }

  hasVideo(message: Message): boolean {
    return (message.videos?.length || 0) > 0;
  }

  // ── Set header ──

  get messageCount(): number {
    return this.workingSet?.messages?.length || 0;
  }

  get setName(): string {
    return this.messageSetForm?.get('name')?.value?.trim() || '';
  }

  get isSetPrivate(): boolean {
    return Boolean(this.messageSetForm?.get('is_private')?.value);
  }

  get setTag(): string {
    return this.messageSetForm?.get('tag')?.value?.trim() || '';
  }

  // Plain state, not a getter off form validity.  Bound to [expanded] a getter
  // is re-evaluated every change detection cycle, so the first character typed
  // into Name made the control valid and slammed the panel shut mid-type.
  // Set once when a set loads, and owned by the user from then on.
  setDetailsExpanded = false;

  // Choice methods
  addBlankChoice(): void {
    if (this.choicesFormArray.length < this.MAX_CHOICES) {
      this.choicesFormArray.push(this.createChoiceFormGroup());
    }
  }

  removeChoice(index: number): void {
    if (this.choicesFormArray.length > 1) {
      this.choicesFormArray.removeAt(index);
    }
  }

  canAddChoice(): boolean {
    return this.choicesFormArray.length < this.MAX_CHOICES;
  }

  // Media methods
  showMediaLibraryModal(): void {
    const config = new MatDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    config.width = '80%';
    config.maxWidth = '1200px';

    const dialogRef = this.dialog.open(MediaLibraryComponent, config);
    dialogRef.componentInstance.dialogRef = dialogRef;
    dialogRef.componentInstance.selectedItems = [
      ...(this.currentMessage?.images || []) as ImageOrVideo[],
      ...(this.currentMessage?.videos || []) as ImageOrVideo[],
    ];

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success && result.selectedItems?.length > 0) {
        if (result.mediaType === MediaType.PHOTOS) {
          this.currentMessage.images = result.selectedItems;
          this.currentMessage.videos = [];
        } else {
          this.currentMessage.videos = result.selectedItems;
          this.currentMessage.images = [];
        }

        // Truncate subject and body if necessary
        this.truncateFieldsForMedia();
      }
    });
  }

  private truncateFieldsForMedia(): void {
    const subjectControl = this.currentMessageForm.get('subject');
    const bodyControl = this.currentMessageForm.get('body');

    if (subjectControl?.value) {
      const maxLength = this.getSubjectMaxLength();
      if (subjectControl.value.length > maxLength) {
        subjectControl.setValue(subjectControl.value.substring(0, maxLength));
      }
    }

    if (bodyControl?.value) {
      const maxLength = this.getBodyMaxLength();
      if (bodyControl.value.length > maxLength) {
        bodyControl.setValue(bodyControl.value.substring(0, maxLength));
      }
    }
  }

  clearMediaSelection(): void {
    this.currentMessage.images = [];
    this.currentMessage.videos = [];
  }

  // Message list methods
  isCurrentMessageInMessageSet(): boolean {
    if (!this.workingSet?.messages) return false;
    return this.workingSet.messages.some(
      (msg) => msg.index === this.currentMessage?.index
    );
  }

  isCurrentMessageDirty(): boolean {
    return this.currentMessageForm.dirty;
  }

  isAddDisabled(): boolean {
    return (
      (this.isCurrentMessageInMessageSet() && !this.isCurrentMessageDirty()) ||
      this.currentMessageForm.invalid
    );
  }

  // Returns the i18n key explaining why saving is blocked, or null when it is
  // not.  A bare boolean left people staring at a greyed-out button with no
  // idea what to do about it.
  saveBlockedReason(): string | null {
    const prefix = 'MESSAGE_SET_BUILDER.SAVE_BLOCKED.';

    if (this.messageSetForm.invalid) {
      return `${prefix}SET_INVALID`;
    }
    if (!this.workingSet?.messages?.length) {
      return `${prefix}NO_MESSAGES`;
    }
    if (this.currentMessageForm.dirty && this.currentMessageForm.invalid) {
      return `${prefix}DRAFT_INVALID`;
    }
    // The data-loss case: an existing message has been edited but not applied.
    // Saving here emitted the set with the message as it was, silently
    // dropping the edit.
    if (this.currentMessageForm.dirty && this.isCurrentMessageInMessageSet()) {
      return `${prefix}UNSAVED_EDIT`;
    }
    if (this.currentMessageForm.valid && !this.isCurrentMessageInMessageSet()) {
      return `${prefix}UNADDED_DRAFT`;
    }
    return null;
  }

  isSaveDisabled(): boolean {
    return this.saveBlockedReason() !== null;
  }

  // Throw away the pending edit: reload the selected message, or reset to a
  // blank draft if this was a new one.
  discardDraft(): void {
    if (this.selectedMessageIndex > -1) {
      this.selectMessage(this.selectedMessageIndex);
    } else {
      this.addBlankMessage();
    }
  }

  addMessageToSet(): void {
    if (this.currentMessageForm.invalid) return;

    const formValue = this.currentMessageForm.value;
    const message: Message = {
      ...this.currentMessage,
      subject: formValue.subject,
      body: formValue.body,
      tag: formValue.tag,
      choices: formValue.choices.map((c: any, idx: number) => ({
        ...c,
        index: idx + 1,
      })),
    };

    if (this.isCurrentMessageInMessageSet()) {
      // Update existing message
      const idx = this.workingSet.messages.findIndex(
        (msg) => msg.index === this.masterCurrentMessage.index
      );
      if (idx > -1) {
        this.workingSet.messages[idx] = message;
      }
    } else {
      // Add new message
      this.workingSet.messages.push(message);
    }

    this.updateIndexes();
    this.addBlankMessage();
  }

  selectMessage(index: number): void {
    if (index >= 0 && index < this.workingSet.messages.length) {
      this.selectedMessageIndex = index;
      this.masterCurrentMessage = this.workingSet.messages[index];
      // A spread would still share choices/images/videos with the set entry,
      // so editing the draft would reach back into the list.
      this.currentMessage = cloneMessage(this.masterCurrentMessage);
      this.populateCurrentMessageForm(this.currentMessage);
      this.currentMessageForm.markAsPristine();
    }
  }

  removeMessage(index: number): void {
    if (index >= 0 && index < this.workingSet.messages.length) {
      this.workingSet.messages.splice(index, 1);
      this.updateIndexes();
      this.addBlankMessage();
    }
  }

  onMessageDrop(event: CdkDragDrop<Message[]>): void {
    moveItemInArray(
      this.workingSet.messages,
      event.previousIndex,
      event.currentIndex
    );
    this.updateIndexes();
  }

  private updateIndexes(): void {
    this.workingSet.messages.forEach((message, idx) => {
      message.index = idx + 1;
      message.choices?.forEach((choice, cidx) => {
        (choice as any).index = cidx + 1;
      });
    });
  }

  // Preview
  showMessageSetPreviewModal(): void {
    const config = new MatDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    config.minWidth = '50vw';
    config.maxWidth = '80vw';
    config.minHeight = '50vh';
    config.maxHeight = '90vh';
    config.data = { messageSet: this.workingSet };

    this.dialog.open(MessageSetPreviewDialogComponent, config);
  }

  // Save/Cancel
  saveMessageSet(): void {
    if (this.isSaveDisabled()) return;

    const formValue = this.messageSetForm.value;
    const messageSetToSave: MessageSet = {
      ...this.workingSet,
      name: formValue.name,
      description: formValue.description,
      tag: formValue.tag,
      is_private: formValue.is_private,
    };

    this.save.emit(messageSetToSave);
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
